import type { Runner } from '@core/types/runner.ts'
import type { EventTracker } from '@core/events/event-manager.ts'
import type { UserStore } from '../storage/user-storage-manager.ts'
import { type Logger, TaggedLogger } from '@core/logging/logger.ts'
import { Env } from '@core/env.ts'
import { TrackedEventAction } from '@core/events/tracked-event-action.ts'
import { SubscriberStatus } from '../enums/subscriber-status.ts'
import { Globals } from '../globals.ts'
import { ensureError } from '@core/utils/ensure-error.ts'
import { SessionStore } from '../storage/session-storage-manager.ts'
import { PageChangeListener } from '../types/page-change-listener.ts'
import { createNavigationObserver, type NavigationObserver } from './navigation/navigation-observer.ts'

/** @internal */
export class PageViewProcessor implements Runner {
    /** @deprecated retained for the polling fallback cadence; see PollingNavigationObserver. */
    public static readonly PAGE_VIEW_INTERVAL_SECONDS = 1

    /**
     * Instantiates a {@link PageViewProcessor} that watches page-view activity.
     *
     * Detection is event-driven via the Navigation API where available, falling back to a
     * 1-second poll. Either way, call [_start]{@link PageViewProcessor#_start} to begin watching.
     */
    public static _init(
        eventManager: EventTracker,
        userStore: UserStore,
        sessionStore: SessionStore,
        pageChangeListener: PageChangeListener | null,
        logger?: Logger,
    ): PageViewProcessor {
        logger ??= TaggedLogger._getInstance('PageViewProcessor')
        return new PageViewProcessor(eventManager, userStore, sessionStore, pageChangeListener, logger)
    }

    private previousSubscriberState: SubscriberStatus | null = null

    /**
     * In-memory marker of the URL we last counted as a page view. Intentionally NOT persisted:
     * each page load (including a reload) starts with a clean marker, so a reload of the same
     * URL counts as a fresh page view. Within a single page lifetime it debounces repeat signals
     * — essential for the polling fallback, and it filters same-URL `replaceState` churn on the
     * Navigation API path.
     */
    private lastPageViewed: string | null = null

    private navigationObserver: NavigationObserver | null = null

    protected constructor(
        private readonly eventManager: EventTracker,
        private readonly userStore: UserStore,
        private readonly sessionStore: SessionStore,
        private readonly pageChangeListener: PageChangeListener | null,
        private readonly logger: Logger,
    ) {
        this.evaluateInitialSubscriberStatus()
    }

    public async evaluateInitialSubscriberStatus() {
        const subscriberStatus = await this.userStore._getSubscriberStatus()
        this.previousSubscriberState ??= subscriberStatus
    }

    /**
     * Begins watching for navigations. Fires an immediate evaluation so the initial load
     * (and every reload) is counted as a page view, then subscribes to the navigation observer.
     */
    public _start(): void {
        if (this.navigationObserver) {
            return
        }

        this.navigationObserver = createNavigationObserver(() => this._run(), this.logger)
        // Immediate evaluation == the current load/reload itself.
        void this._run()
        this.navigationObserver._start()
    }

    /** Stops watching for navigations. */
    public _stop(): void {
        this.navigationObserver?._stop()
        this.navigationObserver = null
    }

    /**
     * Evaluates whether the current location warrants a page-view event. Compares
     * {@link this._currentLocation} against the in-memory last-viewed URL; if different (or a new
     * subscription was just detected) tracks a {@link TrackedEventAction.VIEW_PAGE} event for
     * subscribers. Non-subscribers send no page views.
     *
     * Invoked on start, by the navigation observer, and on a subscribe transition (see
     * {@link PageViewProcessor#_evaluate}).
     */
    public async _run() {
        try {
            const currPage = this._currentLocation.href
            const lastPageViewed = this.lastPageViewed

            // always recheck subscriber status as the user may have manually updated their browser permissions
            // during the current session.
            const subscriberState = await this.userStore._getSubscriberStatus()

            const newSubscriptionDetected =
                subscriberState !== this.previousSubscriberState && subscriberState === SubscriberStatus.SUBSCRIBED
            this.previousSubscriberState = subscriberState

            if (!lastPageViewed || lastPageViewed !== currPage || newSubscriptionDetected) {
                // new subscription in same session is not a true page change event
                if (!newSubscriptionDetected) {
                    this.lastPageViewed = currPage

                    this.logger.verbose('Incrementing page views')
                    await this.sessionStore._incrementSessionPageViewCount()
                }

                this.pageChangeListener?.onPageChanged()

                if (subscriberState === SubscriberStatus.SUBSCRIBED) {
                    this.logger.verbose('Preparing page view event')
                    await this._track(TrackedEventAction.VIEW_PAGE, currPage)
                } else {
                    this.logger.debug('Page views are only tracked for subscribers')
                }
            }
        } catch (err) {
            const error = ensureError(err)
            this.eventManager._track(error)
            this.logger.warn(error)
        }
    }

    /**
     * Re-evaluates page-view state outside of a navigation. Used by the permission-change
     * path so an in-session subscribe (with no navigation) still emits a page view — parity
     * with the legacy poll, which re-checked subscriber status every tick.
     */
    public _evaluate(): Promise<void> {
        return this._run()
    }

    /**
     * Returns value of {@link Env._location}
     */
    protected get _currentLocation() {
        return Env._location
    }

    /**
     * Sends a {@link TrackedEvent} using passed action and page_url data.
     */
    protected async _track(action: TrackedEventAction.VIEW_PAGE, page: string) {
        const viewPageEventData = { [Globals._tevDataPageUrlKey]: page }
        this.eventManager._track(action, viewPageEventData)
    }
}
